import type { CatalogContext, ProductGuardKey } from '@/lib/catalog/types';

export function normalizeCatalogText(value: string) {
  return String(value || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/đ/g, 'd')
    .replace(/[^a-z0-9\s./+-]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function escapeRegExp(value: string) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

export function containsCatalogKeyword(haystack: string, keyword: string) {
  const normalizedHaystack = normalizeCatalogText(haystack);
  const normalizedKeyword = normalizeCatalogText(keyword);
  if (!normalizedHaystack || !normalizedKeyword) return false;

  const phraseRegex = new RegExp(`(?:^|\\s)${escapeRegExp(normalizedKeyword)}(?:$|\\s)`, 'i');
  if (phraseRegex.test(normalizedHaystack)) return true;
  return normalizedHaystack.includes(normalizedKeyword);
}

export function inferProductGuardKey(input: {
  rootSlug?: string;
  childSlug?: string;
}): ProductGuardKey {
  const rootSlug = String(input.rootSlug || '').toLowerCase();
  const childSlug = String(input.childSlug || '').toLowerCase();

  if (childSlug.includes('ssd') || rootSlug === 'ssd') return 'ssd';
  if (childSlug.includes('hdd') || rootSlug === 'hdd') return 'hdd';
  if (childSlug.includes('ram') || rootSlug === 'ram') return 'ram';
  if (
    childSlug.includes('mainboard') ||
    childSlug.includes('bo-mach-chu') ||
    childSlug.includes('motherboard') ||
    rootSlug === 'mainboard'
  )
    return 'mainboard';
  if (childSlug.includes('cpu') || rootSlug === 'cpu') return 'cpu';
  if (childSlug.includes('vga') || rootSlug === 'vga') return 'vga';
  if (
    childSlug.includes('case') ||
    childSlug.includes('vo-may') ||
    childSlug.includes('thung-may') ||
    (rootSlug === 'case' && !childSlug)
  )
    return 'case';
  if (rootSlug === 'laptop') return 'laptop';
  if (
    childSlug.includes('ban-phim') ||
    childSlug.includes('keyboard') ||
    rootSlug === 'ban-phim' ||
    rootSlug === 'keyboard'
  )
    return 'keyboard';
  if (childSlug.includes('chuot') || childSlug.includes('mouse') || rootSlug === 'chuot')
    return 'mouse';
  if (
    childSlug.includes('tai-nghe') ||
    childSlug.includes('headset') ||
    childSlug.includes('headphone') ||
    rootSlug === 'tai-nghe'
  )
    return 'headset';
  if (
    childSlug.includes('mang') ||
    childSlug.includes('networking') ||
    childSlug.includes('wifi') ||
    childSlug.includes('router') ||
    childSlug.includes('switch') ||
    rootSlug === 'thiet-bi-mang'
  )
    return 'networking';
  if (childSlug.includes('ups') || rootSlug === 'ups') return 'ups';
  if (
    childSlug.includes('may-in') ||
    childSlug.includes('printer') ||
    rootSlug === 'may-in' ||
    rootSlug === 'may-in-mau' ||
    rootSlug === 'may-in-den-trang' ||
    rootSlug === 'may-in-ups'
  )
    return 'printer';
  if (
    childSlug.includes('camera') ||
    childSlug.includes('cam ') ||
    rootSlug === 'camera' ||
    rootSlug === 'camera-trong-nha' ||
    rootSlug === 'camera-ngoai-troi' ||
    rootSlug === 'camera-webcam'
  )
    return 'camera';
  if (
    childSlug.includes('man-hinh') ||
    childSlug.includes('monitor') ||
    rootSlug === 'man-hinh' ||
    rootSlug === 'monitor'
  )
    return 'monitor';
  if (
    childSlug.includes('tan-nhiet') ||
    childSlug.includes('cooling') ||
    rootSlug === 'cooling' ||
    rootSlug === 'tan-nhiet'
  )
    return 'cooling';
  if (
    childSlug.includes('nguon') ||
    childSlug.includes('psu') ||
    rootSlug === 'psu' ||
    rootSlug === 'nguon-may-tinh'
  )
    return 'psu';
  if (childSlug.includes('webcam') || rootSlug === 'webcam') return 'webcam';

  return 'default';
}

export function isLikelyProductType(
  haystack: string,
  sku: string,
  tagsText: string,
  type: ProductGuardKey
) {
  const normalizedHaystack = normalizeCatalogText(haystack);
  const normalizedTags = normalizeCatalogText(tagsText);
  const upperSku = String(sku || '').toUpperCase();

  switch (type) {
    case 'ssd':
      return (
        upperSku.startsWith('SSD.') ||
        upperSku.startsWith('SS.') ||
        containsCatalogKeyword(normalizedTags, 'ssd') ||
        containsCatalogKeyword(normalizedHaystack, 'ssd') ||
        containsCatalogKeyword(normalizedHaystack, 'nvme')
      );
    case 'hdd':
      return (
        upperSku.startsWith('H.') ||
        containsCatalogKeyword(normalizedTags, 'hdd') ||
        containsCatalogKeyword(normalizedHaystack, 'hdd') ||
        containsCatalogKeyword(normalizedHaystack, 'hard disk')
      );
    case 'ram':
      return (
        upperSku.startsWith('R3.') ||
        upperSku.startsWith('R4.') ||
        upperSku.startsWith('R5.') ||
        upperSku.startsWith('R.') ||
        containsCatalogKeyword(normalizedTags, 'ram') ||
        containsCatalogKeyword(normalizedHaystack, 'ram') ||
        containsCatalogKeyword(normalizedHaystack, 'ddr')
      );
    case 'cpu':
      return (
        upperSku.startsWith('A.') ||
        upperSku.startsWith('I3.') ||
        upperSku.startsWith('I5.') ||
        upperSku.startsWith('I7.') ||
        upperSku.startsWith('I9.') ||
        upperSku.startsWith('IU3.') ||
        upperSku.startsWith('IU5.') ||
        upperSku.startsWith('IU7.') ||
        upperSku.startsWith('IU9.') ||
        containsCatalogKeyword(normalizedTags, 'cpu') ||
        containsCatalogKeyword(normalizedHaystack, 'cpu') ||
        containsCatalogKeyword(normalizedHaystack, 'processor') ||
        containsCatalogKeyword(normalizedHaystack, 'ryzen') ||
        containsCatalogKeyword(normalizedHaystack, 'core i')
      );
    case 'mainboard':
      return (
        upperSku.startsWith('M.') ||
        containsCatalogKeyword(normalizedTags, 'mainboard') ||
        containsCatalogKeyword(normalizedHaystack, 'mainboard') ||
        containsCatalogKeyword(normalizedHaystack, 'motherboard') ||
        containsCatalogKeyword(normalizedHaystack, 'bo mach chu')
      );
    case 'vga':
      return (
        upperSku.startsWith('V.') ||
        containsCatalogKeyword(normalizedTags, 'vga') ||
        containsCatalogKeyword(normalizedHaystack, 'vga') ||
        containsCatalogKeyword(normalizedHaystack, 'gpu') ||
        containsCatalogKeyword(normalizedHaystack, 'card man hinh') ||
        containsCatalogKeyword(normalizedHaystack, 'rtx') ||
        containsCatalogKeyword(normalizedHaystack, 'gtx') ||
        containsCatalogKeyword(normalizedHaystack, 'radeon')
      );
    case 'case':
      return (
        upperSku.startsWith('CA.') ||
        containsCatalogKeyword(normalizedTags, 'case') ||
        containsCatalogKeyword(normalizedHaystack, 'case') ||
        containsCatalogKeyword(normalizedHaystack, 'thung may') ||
        containsCatalogKeyword(normalizedHaystack, 'vo may')
      );
    case 'laptop':
      return containsCatalogKeyword(normalizedHaystack, 'laptop');
    case 'keyboard':
      return (
        upperSku.startsWith('KB.') ||
        containsCatalogKeyword(normalizedTags, 'ban phim') ||
        containsCatalogKeyword(normalizedTags, 'keyboard') ||
        containsCatalogKeyword(normalizedHaystack, 'ban phim') ||
        containsCatalogKeyword(normalizedHaystack, 'keyboard')
      );
    case 'mouse':
      return (
        containsCatalogKeyword(normalizedTags, 'chuot') ||
        containsCatalogKeyword(normalizedTags, 'mouse') ||
        containsCatalogKeyword(normalizedHaystack, 'chuot') ||
        containsCatalogKeyword(normalizedHaystack, 'mouse')
      );
    case 'headset':
      return (
        containsCatalogKeyword(normalizedTags, 'tai nghe') ||
        containsCatalogKeyword(normalizedTags, 'headset') ||
        containsCatalogKeyword(normalizedHaystack, 'tai nghe') ||
        containsCatalogKeyword(normalizedHaystack, 'headset')
      );
    case 'networking':
      return (
        containsCatalogKeyword(normalizedTags, 'thiet bi mang') ||
        containsCatalogKeyword(normalizedTags, 'wifi') ||
        containsCatalogKeyword(normalizedHaystack, 'router') ||
        containsCatalogKeyword(normalizedHaystack, 'switch mang') ||
        containsCatalogKeyword(normalizedHaystack, 'phat wifi')
      );
    case 'printer':
      return (
        containsCatalogKeyword(normalizedTags, 'may in') ||
        containsCatalogKeyword(normalizedTags, 'printer') ||
        containsCatalogKeyword(normalizedHaystack, 'may in') ||
        containsCatalogKeyword(normalizedHaystack, 'printer')
      );
    case 'ups':
      return (
        containsCatalogKeyword(normalizedTags, 'ups') ||
        containsCatalogKeyword(normalizedTags, 'bo luu dien') ||
        containsCatalogKeyword(normalizedHaystack, 'ups') ||
        containsCatalogKeyword(normalizedHaystack, 'bo luu dien')
      );
    case 'camera':
      return (
        containsCatalogKeyword(normalizedTags, 'camera') ||
        containsCatalogKeyword(normalizedTags, 'webcam') ||
        containsCatalogKeyword(normalizedHaystack, 'camera') ||
        containsCatalogKeyword(normalizedHaystack, 'webcam')
      );
    case 'monitor':
      return (
        upperSku.startsWith('MN.') ||
        containsCatalogKeyword(normalizedTags, 'man hinh') ||
        containsCatalogKeyword(normalizedTags, 'monitor') ||
        containsCatalogKeyword(normalizedHaystack, 'man hinh') ||
        containsCatalogKeyword(normalizedHaystack, 'monitor') ||
        containsCatalogKeyword(normalizedHaystack, 'panel')
      );
    case 'cooling':
      return (
        upperSku.startsWith('CL.') ||
        containsCatalogKeyword(normalizedTags, 'tan nhiet') ||
        containsCatalogKeyword(normalizedTags, 'cooling') ||
        containsCatalogKeyword(normalizedHaystack, 'tan khi') ||
        containsCatalogKeyword(normalizedHaystack, 'tan nuoc') ||
        containsCatalogKeyword(normalizedHaystack, 'cooler')
      );
    case 'psu':
      return (
        upperSku.startsWith('PS.') ||
        containsCatalogKeyword(normalizedTags, 'nguon') ||
        containsCatalogKeyword(normalizedTags, 'psu') ||
        containsCatalogKeyword(normalizedHaystack, 'nguon may tinh') ||
        containsCatalogKeyword(normalizedHaystack, 'psu') ||
        containsCatalogKeyword(normalizedHaystack, 'power supply')
      );
    case 'webcam':
      return (
        containsCatalogKeyword(normalizedTags, 'webcam') ||
        containsCatalogKeyword(normalizedHaystack, 'webcam')
      );
    default:
      return true;
  }
}

export function buildSubTypeGuardSql(subSlug: string) {
  const lowerSlug = String(subSlug || '').toLowerCase();

  if (lowerSlug === 'cpu-intel') {
    return {
      sql: `(
        UPPER(COALESCE(sku, '')) LIKE ?
        OR UPPER(COALESCE(sku, '')) LIKE ?
        OR UPPER(COALESCE(sku, '')) LIKE ?
        OR UPPER(COALESCE(sku, '')) LIKE ?
        OR UPPER(COALESCE(sku, '')) LIKE ?
        OR UPPER(COALESCE(sku, '')) LIKE ?
        OR UPPER(COALESCE(sku, '')) LIKE ?
        OR UPPER(COALESCE(sku, '')) LIKE ?
        OR LOWER(COALESCE(name, '')) LIKE ?
        OR LOWER(COALESCE(specs, '')) LIKE ?
        OR LOWER(CAST(tags AS CHAR)) LIKE ?
      )`,
      params: [
        'I3.%',
        'I5.%',
        'I7.%',
        'I9.%',
        'IU3.%',
        'IU5.%',
        'IU7.%',
        'IU9.%',
        '%intel%',
        '%intel%',
        '%subcategory:cpu-intel%',
      ],
    };
  }

  if (lowerSlug === 'cpu-amd') {
    return {
      sql: `(
        UPPER(COALESCE(sku, '')) LIKE ?
        OR LOWER(COALESCE(name, '')) LIKE ?
        OR LOWER(COALESCE(name, '')) LIKE ?
        OR LOWER(COALESCE(specs, '')) LIKE ?
        OR LOWER(CAST(tags AS CHAR)) LIKE ?
      )`,
      params: ['A.%', '%amd%', '%ryzen%', '%ryzen%', '%subcategory:cpu-amd%'],
    };
  }

  if (lowerSlug === 'ram-ddr4' || lowerSlug === 'ram-ddr5') {
    const ramType = lowerSlug.endsWith('ddr4') ? 'ddr4' : 'ddr5';
    const skuPrefix = lowerSlug.endsWith('ddr4') ? 'R4.%' : 'R5.%';
    return {
      sql: `(
        UPPER(COALESCE(sku, '')) LIKE ?
        OR LOWER(COALESCE(name, '')) LIKE ?
        OR LOWER(COALESCE(specs, '')) LIKE ?
        OR LOWER(CAST(tags AS CHAR)) LIKE ?
      )`,
      params: [skuPrefix, `%${ramType}%`, `%${ramType}%`, `%subcategory:${lowerSlug}%`],
    };
  }

  if (lowerSlug === 'ssd-nvme') {
    return {
      sql: `(
        LOWER(COALESCE(name, '')) LIKE ?
        OR LOWER(COALESCE(specs, '')) LIKE ?
        OR LOWER(CAST(tags AS CHAR)) LIKE ?
      )`,
      params: ['%nvme%', '%nvme%', '%subcategory:ssd-nvme%'],
    };
  }

  if (lowerSlug === 'ssd-sata') {
    return {
      sql: `(
        LOWER(COALESCE(name, '')) LIKE ?
        OR LOWER(COALESCE(specs, '')) LIKE ?
        OR LOWER(CAST(tags AS CHAR)) LIKE ?
      )`,
      params: ['%sata%', '%sata%', '%subcategory:ssd-sata%'],
    };
  }

  if (lowerSlug === 'mainboard-intel') {
    return {
      sql: `(
        LOWER(COALESCE(name, '')) LIKE ?
        OR LOWER(COALESCE(name, '')) LIKE ?
        OR LOWER(COALESCE(name, '')) LIKE ?
        OR LOWER(COALESCE(name, '')) LIKE ?
        OR LOWER(COALESCE(specs, '')) LIKE ?
        OR LOWER(CAST(tags AS CHAR)) LIKE ?
      )`,
      params: ['%intel%', '%h610%', '%b760%', '%z790%', '%intel%', '%subcategory:mainboard-intel%'],
    };
  }

  if (lowerSlug === 'mainboard-amd') {
    return {
      sql: `(
        LOWER(COALESCE(name, '')) LIKE ?
        OR LOWER(COALESCE(name, '')) LIKE ?
        OR LOWER(COALESCE(name, '')) LIKE ?
        OR LOWER(COALESCE(name, '')) LIKE ?
        OR LOWER(COALESCE(name, '')) LIKE ?
        OR LOWER(COALESCE(specs, '')) LIKE ?
        OR LOWER(CAST(tags AS CHAR)) LIKE ?
      )`,
      params: [
        '%amd%',
        '%am4%',
        '%am5%',
        '%b550%',
        '%b650%',
        '%amd%',
        '%subcategory:mainboard-amd%',
      ],
    };
  }

  if (lowerSlug === 'vga-nvidia') {
    return {
      sql: `(
        LOWER(COALESCE(name, '')) LIKE ?
        OR LOWER(COALESCE(name, '')) LIKE ?
        OR LOWER(COALESCE(name, '')) LIKE ?
        OR LOWER(COALESCE(specs, '')) LIKE ?
        OR LOWER(CAST(tags AS CHAR)) LIKE ?
      )`,
      params: ['%nvidia%', '%geforce%', '%rtx%', '%nvidia%', '%subcategory:vga-nvidia%'],
    };
  }

  if (lowerSlug === 'vga-amd') {
    return {
      sql: `(
        LOWER(COALESCE(name, '')) LIKE ?
        OR LOWER(COALESCE(name, '')) LIKE ?
        OR LOWER(COALESCE(specs, '')) LIKE ?
        OR LOWER(CAST(tags AS CHAR)) LIKE ?
      )`,
      params: ['%radeon%', '% rx%', '%radeon%', '%subcategory:vga-amd%'],
    };
  }

  if (
    lowerSlug === 'laptop-gaming' ||
    lowerSlug === 'laptop-van-phong' ||
    lowerSlug === 'laptop-ai'
  ) {
    const keyword =
      lowerSlug === 'laptop-gaming' ? 'gaming' : lowerSlug === 'laptop-ai' ? 'ai' : 'van phong';
    return {
      sql: `(
        LOWER(COALESCE(name, '')) LIKE ?
        OR LOWER(COALESCE(specs, '')) LIKE ?
        OR LOWER(CAST(tags AS CHAR)) LIKE ?
      )`,
      params: [`%${keyword}%`, `%${keyword}%`, `%subcategory:${lowerSlug}%`],
    };
  }

  if (lowerSlug === 'case-gaming' || lowerSlug === 'case-van-phong') {
    const keyword = lowerSlug.endsWith('gaming') ? 'gaming' : 'van phong';
    return {
      sql: `(
        LOWER(COALESCE(name, '')) LIKE ?
        OR LOWER(COALESCE(specs, '')) LIKE ?
        OR LOWER(CAST(tags AS CHAR)) LIKE ?
      )`,
      params: [`%${keyword}%`, `%${keyword}%`, `%subcategory:${lowerSlug}%`],
    };
  }

  if (lowerSlug === 'router-wifi') {
    return {
      sql: `(
        (
          LOWER(COALESCE(name, '')) LIKE ?
          OR LOWER(COALESCE(name, '')) LIKE ?
          OR LOWER(COALESCE(name, '')) LIKE ?
          OR LOWER(CAST(tags AS CHAR)) LIKE ?
        )
        AND LOWER(COALESCE(name, '')) NOT LIKE ?
        AND LOWER(COALESCE(name, '')) NOT LIKE ?
        AND LOWER(COALESCE(name, '')) NOT LIKE ?
        AND LOWER(COALESCE(name, '')) NOT LIKE ?
      )`,
      params: [
        '%router%',
        '%phát wifi%',
        '%phat wifi%',
        '%subcategory:router-wifi%',
        '%usb%',
        '%switch%',
        '%cáp%',
        '%card mạng%',
      ],
    };
  }

  if (lowerSlug === 'switch') {
    return {
      sql: `(
        LOWER(COALESCE(name, '')) LIKE ?
        OR LOWER(COALESCE(specs, '')) LIKE ?
        OR LOWER(CAST(tags AS CHAR)) LIKE ?
      )`,
      params: ['%switch%', '%switch%', '%subcategory:switch%'],
    };
  }

  if (lowerSlug === 'usb-wifi') {
    return {
      sql: `(
        LOWER(COALESCE(name, '')) LIKE ?
        OR LOWER(COALESCE(specs, '')) LIKE ?
        OR LOWER(CAST(tags AS CHAR)) LIKE ?
      )`,
      params: ['%usb wifi%', '%usb wifi%', '%subcategory:usb-wifi%'],
    };
  }

  if (lowerSlug === 'ups') {
    return {
      sql: `(
        LOWER(COALESCE(name, '')) LIKE ?
        OR LOWER(COALESCE(name, '')) LIKE ?
        OR LOWER(COALESCE(specs, '')) LIKE ?
        OR LOWER(CAST(tags AS CHAR)) LIKE ?
      )`,
      params: ['%ups%', '%bo luu dien%', '%ups%', '%subcategory:ups%'],
    };
  }

  if (lowerSlug === 'may-in-mau') {
    return {
      sql: `(
        LOWER(COALESCE(name, '')) LIKE ?
        OR LOWER(COALESCE(name, '')) LIKE ?
        OR LOWER(COALESCE(name, '')) LIKE ?
        OR LOWER(COALESCE(specs, '')) LIKE ?
        OR LOWER(CAST(tags AS CHAR)) LIKE ?
      )`,
      params: ['%màu%', '%mau%', '%phun%', '%color%', '%subcategory:may-in-mau%'],
    };
  }

  if (lowerSlug === 'may-in-den-trang') {
    return {
      sql: `(
        (
          LOWER(COALESCE(name, '')) LIKE ?
          OR LOWER(COALESCE(name, '')) LIKE ?
          OR LOWER(COALESCE(name, '')) LIKE ?
          OR LOWER(COALESCE(name, '')) LIKE ?
          OR LOWER(CAST(tags AS CHAR)) LIKE ?
        )
        AND LOWER(COALESCE(name, '')) NOT LIKE ?
        AND LOWER(COALESCE(name, '')) NOT LIKE ?
      )`,
      params: [
        '%đen trắng%',
        '%trắng đen%',
        '%den trang%',
        '%đơn sắc%',
        '%subcategory:may-in-den-trang%',
        '%màu%',
        '%phun màu%',
      ],
    };
  }

  if (lowerSlug === 'camera-trong-nha') {
    return {
      sql: `(
        (
          LOWER(COALESCE(name, '')) LIKE ?
          OR LOWER(COALESCE(name, '')) LIKE ?
          OR LOWER(CAST(tags AS CHAR)) LIKE ?
        )
        AND LOWER(COALESCE(name, '')) NOT LIKE ?
        AND LOWER(COALESCE(name, '')) NOT LIKE ?
      )`,
      params: [
        '%trong nhà%',
        '%trong nha%',
        '%subcategory:camera-trong-nha%',
        '%webcam%',
        '%ngoài trời%',
      ],
    };
  }

  if (lowerSlug === 'camera-ngoai-troi') {
    return {
      sql: `(
        (
          LOWER(COALESCE(name, '')) LIKE ?
          OR LOWER(COALESCE(name, '')) LIKE ?
          OR LOWER(CAST(tags AS CHAR)) LIKE ?
        )
        AND LOWER(COALESCE(name, '')) NOT LIKE ?
        AND LOWER(COALESCE(name, '')) NOT LIKE ?
      )`,
      params: [
        '%ngoài trời%',
        '%ngoai troi%',
        '%subcategory:camera-ngoai-troi%',
        '%webcam%',
        '%trong nhà%',
      ],
    };
  }

  if (lowerSlug === 'webcam') {
    return {
      sql: `(
        LOWER(COALESCE(name, '')) LIKE ?
        OR LOWER(COALESCE(specs, '')) LIKE ?
        OR LOWER(CAST(tags AS CHAR)) LIKE ?
      )`,
      params: ['%webcam%', '%webcam%', '%subcategory:webcam%'],
    };
  }

  if (lowerSlug.includes('ssd')) {
    return {
      sql: `(
        UPPER(COALESCE(sku, '')) LIKE ?
        OR UPPER(COALESCE(sku, '')) LIKE ?
        OR LOWER(COALESCE(name, '')) LIKE ?
        OR LOWER(COALESCE(specs, '')) LIKE ?
        OR LOWER(COALESCE(description, '')) LIKE ?
      )`,
      params: ['SSD.%', 'SS.%', '%ssd%', '%ssd%', '%ssd%'],
    };
  }

  if (lowerSlug.includes('hdd')) {
    return {
      sql: `(
        UPPER(COALESCE(sku, '')) LIKE ?
        OR LOWER(COALESCE(name, '')) LIKE ?
        OR LOWER(COALESCE(specs, '')) LIKE ?
        OR LOWER(COALESCE(description, '')) LIKE ?
      )`,
      params: ['H.%', '%hdd%', '%hdd%', '%hard disk%'],
    };
  }

  if (lowerSlug.includes('ram')) {
    return {
      sql: `(
        UPPER(COALESCE(sku, '')) LIKE ?
        OR UPPER(COALESCE(sku, '')) LIKE ?
        OR UPPER(COALESCE(sku, '')) LIKE ?
        OR LOWER(COALESCE(name, '')) LIKE ?
        OR LOWER(COALESCE(specs, '')) LIKE ?
      )`,
      params: ['R3.%', 'R4.%', 'R5.%', '%ram%', '%ddr%'],
    };
  }

  if (lowerSlug.includes('mainboard') || lowerSlug.includes('bo-mach-chu')) {
    return {
      sql: `(
        UPPER(COALESCE(sku, '')) LIKE ?
        OR LOWER(COALESCE(name, '')) LIKE ?
        OR LOWER(COALESCE(name, '')) LIKE ?
        OR LOWER(CAST(tags AS CHAR)) LIKE ?
        OR LOWER(CAST(tags AS CHAR)) LIKE ?
      )`,
      params: [
        'M.%',
        '%mainboard%',
        '%bo mach%',
        '%subcategory:mainboard%',
        '%category:mainboard%',
      ],
    };
  }

  if (lowerSlug.includes('cpu')) {
    return {
      sql: `(
        UPPER(COALESCE(sku, '')) LIKE ?
        OR UPPER(COALESCE(sku, '')) LIKE ?
        OR UPPER(COALESCE(sku, '')) LIKE ?
        OR UPPER(COALESCE(sku, '')) LIKE ?
        OR UPPER(COALESCE(sku, '')) LIKE ?
        OR UPPER(COALESCE(sku, '')) LIKE ?
        OR UPPER(COALESCE(sku, '')) LIKE ?
        OR UPPER(COALESCE(sku, '')) LIKE ?
        OR UPPER(COALESCE(sku, '')) LIKE ?
        OR LOWER(COALESCE(name, '')) LIKE ?
        OR LOWER(COALESCE(name, '')) LIKE ?
        OR LOWER(CAST(tags AS CHAR)) LIKE ?
        OR LOWER(CAST(tags AS CHAR)) LIKE ?
      )`,
      params: [
        'A.%',
        'I3.%',
        'I5.%',
        'I7.%',
        'I9.%',
        'IU3.%',
        'IU5.%',
        'IU7.%',
        'IU9.%',
        '%cpu%',
        '%ryzen%',
        '%subcategory:cpu%',
        '%category:cpu%',
      ],
    };
  }

  if (lowerSlug.includes('vga') || lowerSlug.includes('card-man-hinh')) {
    return {
      sql: `(
        UPPER(COALESCE(sku, '')) LIKE ?
        OR LOWER(COALESCE(name, '')) LIKE ?
        OR LOWER(COALESCE(name, '')) LIKE ?
        OR LOWER(COALESCE(name, '')) LIKE ?
        OR LOWER(COALESCE(specs, '')) LIKE ?
        OR LOWER(CAST(tags AS CHAR)) LIKE ?
        OR LOWER(CAST(tags AS CHAR)) LIKE ?
      )`,
      params: [
        'V.%',
        '%vga%',
        '%rtx%',
        '%radeon%',
        '%card man hinh%',
        '%subcategory:vga%',
        '%category:vga%',
      ],
    };
  }

  if (
    lowerSlug.includes('case') ||
    lowerSlug.includes('vo-may') ||
    lowerSlug.includes('thung-may')
  ) {
    return {
      sql: `(
        UPPER(COALESCE(sku, '')) LIKE ?
        OR LOWER(COALESCE(name, '')) LIKE ?
        OR LOWER(COALESCE(specs, '')) LIKE ?
        OR LOWER(COALESCE(description, '')) LIKE ?
      )`,
      params: ['CA.%', '%case%', '%thung may%', '%vo may%'],
    };
  }

  if (lowerSlug.includes('ban-phim') || lowerSlug.includes('keyboard')) {
    return {
      sql: `(
        (
          UPPER(COALESCE(sku, '')) LIKE ?
          OR LOWER(COALESCE(name, '')) LIKE ?
          OR LOWER(COALESCE(name, '')) LIKE ?
          OR LOWER(COALESCE(specs, '')) LIKE ?
          OR LOWER(CAST(tags AS CHAR)) LIKE ?
          OR LOWER(CAST(tags AS CHAR)) LIKE ?
        )
        AND LOWER(COALESCE(name, '')) NOT LIKE ?
      )`,
      params: [
        'KB.%',
        '%ban phim%',
        '%keyboard%',
        '%keyboard%',
        '%subcategory:ban-phim%',
        '%category:ban-phim%',
        '%combo%',
      ],
    };
  }

  if (lowerSlug.includes('chuot') || lowerSlug.includes('mouse')) {
    return {
      sql: `(
        (
          LOWER(COALESCE(name, '')) LIKE ?
          OR LOWER(COALESCE(name, '')) LIKE ?
          OR LOWER(COALESCE(specs, '')) LIKE ?
          OR LOWER(CAST(tags AS CHAR)) LIKE ?
          OR LOWER(CAST(tags AS CHAR)) LIKE ?
        )
        AND LOWER(COALESCE(name, '')) NOT LIKE ?
      )`,
      params: [
        '%chuot%',
        '%mouse%',
        '%mouse%',
        '%subcategory:chuot%',
        '%category:chuot%',
        '%combo%',
      ],
    };
  }

  if (lowerSlug.includes('tai-nghe') || lowerSlug.includes('headset')) {
    return {
      sql: `(
        LOWER(COALESCE(name, '')) LIKE ?
        OR LOWER(COALESCE(name, '')) LIKE ?
        OR LOWER(COALESCE(name, '')) LIKE ?
        OR LOWER(COALESCE(specs, '')) LIKE ?
        OR LOWER(CAST(tags AS CHAR)) LIKE ?
        OR LOWER(CAST(tags AS CHAR)) LIKE ?
      )`,
      params: [
        '%tai nghe%',
        '%headset%',
        '%headphone%',
        '%headset%',
        '%subcategory:tai-nghe%',
        '%category:tai-nghe%',
      ],
    };
  }

  if (lowerSlug.includes('may-in') || lowerSlug.includes('printer')) {
    return {
      sql: `(
        LOWER(COALESCE(name, '')) LIKE ?
        OR LOWER(COALESCE(specs, '')) LIKE ?
        OR LOWER(COALESCE(description, '')) LIKE ?
      )`,
      params: ['%may in%', '%printer%', '%canon%'],
    };
  }

  if (lowerSlug.includes('camera')) {
    return {
      sql: `(
        LOWER(COALESCE(name, '')) LIKE ?
        OR LOWER(COALESCE(specs, '')) LIKE ?
        OR LOWER(COALESCE(description, '')) LIKE ?
      )`,
      params: ['%camera%', '%webcam%', '%ezviz%'],
    };
  }

  if (lowerSlug.includes('monitor') || lowerSlug.includes('man-hinh')) {
    return {
      sql: `(
        UPPER(COALESCE(sku, '')) LIKE ?
        OR LOWER(COALESCE(name, '')) LIKE ?
        OR LOWER(COALESCE(specs, '')) LIKE ?
        OR LOWER(COALESCE(description, '')) LIKE ?
      )`,
      params: ['MN.%', '%man hinh%', '%monitor%', '%panel%'],
    };
  }

  if (
    lowerSlug.includes('cooling') ||
    lowerSlug.includes('tan-nhiet') ||
    lowerSlug.includes('quat')
  ) {
    return {
      sql: `(
        UPPER(COALESCE(sku, '')) LIKE ?
        OR LOWER(COALESCE(name, '')) LIKE ?
        OR LOWER(COALESCE(specs, '')) LIKE ?
        OR LOWER(COALESCE(description, '')) LIKE ?
      )`,
      params: ['CL.%', '%tan nhiet%', '%cooling%', '%cooler%'],
    };
  }

  if (lowerSlug.includes('psu') || lowerSlug.includes('nguon')) {
    return {
      sql: `(
        UPPER(COALESCE(sku, '')) LIKE ?
        OR LOWER(COALESCE(name, '')) LIKE ?
        OR LOWER(COALESCE(specs, '')) LIKE ?
        OR LOWER(COALESCE(description, '')) LIKE ?
      )`,
      params: ['PS.%', '%nguon%', '%psu%', '%power supply%'],
    };
  }

  return { sql: '1 = 1', params: [] as string[] };
}

export function getProductGuards(context: CatalogContext) {
  return {
    key: context.productGuardKey,
    sql: buildSubTypeGuardSql(context.selectedChild?.slug || context.sub),
  };
}
